import {Client} from './client';
import {EndCustomer} from './end-customer';
import {InvoiceState} from './invoice-state';
import {LeadSource} from './lead-source';
import {Partner} from './partner';
import {Model} from './model';
import {EndCustomerContact} from './end-customer-contact';
import {Unserializer} from './serialization';

export enum InvoicePaymentStatus {
  OPEN = 'open',
  OVERDUE = 'overdue',
  PAID = 'paid'
}

export class Invoice implements Model {
  amount: number;
  amountFinanced?: number;
  bankAccount: string;
  client: Client;
  clientId: number;
  clientRepresentativeId: number;
  created: Date;
  dailyInterest?: number;
  dateFinanced?: Date;
  dueDate?: Date;
  endCustomer: EndCustomer;
  endCustomerId: number;
  endCustomerContact: EndCustomerContact;
  endCustomerContactId?: number;
  finalPaymentAmount?: number;
  finalPaymentDate?: Date;
  financedDays?: number;
  financingFee?: number;
  financingMethodId?: string;
  fixedInterest?: number;
  guarantee?: number;
  expressPayment: boolean;
  expressPaymentFee?: number;
  invoiceConfigurationId: number;
  invoiceId: number;
  invoiceState: InvoiceState;
  invoiceStateId: number;
  lastPaymentDate?: Date;
  leadProvider: Partner;
  leadProviderId: number;
  leadSource: LeadSource;
  leadSourceId: number;
  number: number;
  newInvoiceNumber: string;
  newReferenceNumber: string;
  penaltyInterestFee?: number;
  penaltyInterestMethodId?: string;
  responsibleSeller: Partner;
  responsibleSellerId: number;
  serviceFee?: number;
  serviceFeeVat?: number;
  subfinancingCost: number;
  totalPaidAmount?: number;
  totalProfit?: number;

  static copy(oldObj: Invoice): Invoice {
    const newObj = new Invoice();
    newObj.amount = oldObj.amount;
    newObj.amountFinanced = oldObj.amountFinanced;
    newObj.bankAccount = oldObj.bankAccount;
    newObj.client = oldObj.client;
    newObj.clientId = oldObj.clientId;
    newObj.clientRepresentativeId = oldObj.clientRepresentativeId;
    newObj.created = oldObj.created;
    newObj.dateFinanced = Unserializer.date(oldObj.dateFinanced);
    newObj.dailyInterest = oldObj.dailyInterest;
    newObj.dueDate = Unserializer.date(oldObj.dueDate);
    newObj.endCustomer = oldObj.endCustomer;
    newObj.endCustomerId = oldObj.endCustomerId;
    newObj.endCustomerContact = oldObj.endCustomerContact;
    newObj.endCustomerContactId = oldObj.endCustomerContactId;
    newObj.finalPaymentAmount = oldObj.finalPaymentAmount;
    newObj.finalPaymentDate = Unserializer.date(oldObj.finalPaymentDate);
    newObj.expressPayment = oldObj.expressPayment;
    newObj.expressPaymentFee = oldObj.expressPaymentFee;
    newObj.financedDays = oldObj.financedDays;
    newObj.financingFee = oldObj.financingFee;
    newObj.financingMethodId = oldObj.financingMethodId;
    newObj.fixedInterest = oldObj.fixedInterest;
    newObj.guarantee = oldObj.guarantee;
    newObj.invoiceConfigurationId = oldObj.invoiceConfigurationId;
    newObj.invoiceId = oldObj.invoiceId;
    newObj.invoiceState = oldObj.invoiceState;
    newObj.invoiceStateId = oldObj.invoiceStateId;
    newObj.lastPaymentDate = Unserializer.date(oldObj.lastPaymentDate);
    newObj.leadProvider = oldObj.leadProvider;
    newObj.leadProviderId = oldObj.leadProviderId;
    newObj.leadSource = oldObj.leadSource;
    newObj.leadSourceId = oldObj.leadSourceId;
    newObj.number = oldObj.number;
    newObj.newInvoiceNumber = oldObj.newInvoiceNumber;
    newObj.newReferenceNumber = oldObj.newReferenceNumber;
    newObj.penaltyInterestFee = oldObj.penaltyInterestFee;
    newObj.penaltyInterestMethodId = oldObj.penaltyInterestMethodId;
    newObj.responsibleSeller = oldObj.responsibleSeller;
    newObj.responsibleSellerId = oldObj.responsibleSellerId;
    newObj.serviceFee = oldObj.serviceFee;
    newObj.serviceFeeVat = oldObj.serviceFeeVat;
    newObj.subfinancingCost = oldObj.subfinancingCost;
    newObj.totalPaidAmount = oldObj.totalPaidAmount;
    newObj.totalProfit = oldObj.totalProfit;
    return newObj;
  }

  getPaymentStatus(): InvoicePaymentStatus {
    if (this.totalPaidAmount >= this.amount) {
      return InvoicePaymentStatus.PAID;
    }
    const now = new Date();
    if (this.dueDate !== null && now > this.dueDate) {
      return InvoicePaymentStatus.OVERDUE;
    }
    return InvoicePaymentStatus.OPEN;
  }

  getTotalOpen(): number {
    return this.amount - this.totalPaidAmount;
  }

  getPrimaryKey() {
    return this.invoiceId;
  }

  isNew(): boolean {
    return isNaN(this.invoiceId);
  }

  clone(): Invoice {
    return Invoice.copy(this);
  }
}
